import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit, Trash2, Send, Mail, Users, Clock, Upload, Play, Pause, ChevronDown, ChevronUp, Eye, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const TRIGGERS = [
  { value: 'registration', label: 'New Registration' },
  { value: 'appointment', label: 'Appointment Booked' },
  { value: 'manual', label: 'Manual Enrollment' },
];

export default function AdminEmailCampaigns() {
  const [sequences, setSequences] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sequences');
  
  // Expanded sequences (to show email details inline)
  const [expandedSequence, setExpandedSequence] = useState(null);
  
  // Sequence modal
  const [sequenceModal, setSequenceModal] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [sequenceForm, setSequenceForm] = useState({
    name: '',
    description: '',
    trigger: 'registration',
    is_active: true,
    steps: [],
  });
  
  // Email preview modal
  const [previewModal, setPreviewModal] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  
  // Broadcast modal
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '',
    html_content: '',
    target: 'all',
  });
  const [sending, setSending] = useState(false);
  
  // Import modal
  const [importModal, setImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchData = useCallback(async () => {
    try {
      const [seqs, conts, emailStats] = await Promise.all([
        apiRequest('/admin/email-sequences'),
        apiRequest('/admin/marketing-contacts'),
        apiRequest('/admin/email-stats'),
      ]);
      setSequences(seqs);
      setContacts(conts);
      setStats(emailStats);
      // Auto-expand if only one sequence
      if (seqs.length === 1) {
        setExpandedSequence(seqs[0].id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Sequence handlers
  const openSequenceModal = (sequence = null) => {
    if (sequence) {
      setEditingSequence(sequence);
      setSequenceForm({
        name: sequence.name,
        description: sequence.description || '',
        trigger: sequence.trigger,
        is_active: sequence.is_active,
        steps: sequence.steps || [],
      });
    } else {
      setEditingSequence(null);
      setSequenceForm({
        name: '',
        description: '',
        trigger: 'registration',
        is_active: true,
        steps: [],
      });
    }
    setSequenceModal(true);
  };

  const addStep = () => {
    setSequenceForm(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        { subject: '', html_content: '', delay_days: prev.steps.length === 0 ? 0 : 1 }
      ]
    }));
  };

  const removeStep = (index) => {
    setSequenceForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const updateStep = (index, field, value) => {
    setSequenceForm(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const saveSequence = async () => {
    if (!sequenceForm.name || sequenceForm.steps.length === 0) {
      toast.error('Please add a name and at least one email step');
      return;
    }
    
    try {
      if (editingSequence) {
        await apiRequest(`/admin/email-sequences/${editingSequence.id}`, {
          method: 'PUT',
          body: JSON.stringify(sequenceForm),
        });
        toast.success('Sequence updated');
      } else {
        await apiRequest('/admin/email-sequences', {
          method: 'POST',
          body: JSON.stringify(sequenceForm),
        });
        toast.success('Sequence created');
      }
      fetchData();
      setSequenceModal(false);
    } catch (error) {
      toast.error('Failed to save sequence');
    }
  };

  const deleteSequence = async (id) => {
    if (!window.confirm('Delete this sequence?')) return;
    try {
      await apiRequest(`/admin/email-sequences/${id}`, { method: 'DELETE' });
      setSequences(sequences.filter(s => s.id !== id));
      toast.success('Sequence deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleSequence = async (sequence) => {
    try {
      await apiRequest(`/admin/email-sequences/${sequence.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...sequence, is_active: !sequence.is_active }),
      });
      setSequences(sequences.map(s => 
        s.id === sequence.id ? { ...s, is_active: !s.is_active } : s
      ));
      toast.success(sequence.is_active ? 'Sequence paused' : 'Sequence activated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Quick edit a single email in a sequence
  const quickEditEmail = (sequence, stepIndex) => {
    setEditingSequence(sequence);
    setSequenceForm({
      name: sequence.name,
      description: sequence.description || '',
      trigger: sequence.trigger,
      is_active: sequence.is_active,
      steps: sequence.steps || [],
    });
    setSequenceModal(true);
  };

  // Preview email
  const openPreview = (step, index) => {
    setPreviewEmail({ ...step, index: index + 1 });
    setPreviewModal(true);
  };

  // Broadcast handlers
  const sendBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.html_content) {
      toast.error('Please fill in subject and content');
      return;
    }
    
    setSending(true);
    try {
      const result = await apiRequest('/admin/broadcast-email', {
        method: 'POST',
        body: JSON.stringify(broadcastForm),
      });
      toast.success(result.message);
      setBroadcastModal(false);
      setBroadcastForm({ subject: '', html_content: '', target: 'all' });
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  // Import handlers
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/marketing-contacts/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      const result = await response.json();
      toast.success(`Imported ${result.imported} contacts, ${result.skipped} skipped`);
      fetchData();
      setImportModal(false);
    } catch (error) {
      toast.error('Failed to import');
    } finally {
      setImporting(false);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      await apiRequest(`/admin/marketing-contacts/${id}`, { method: 'DELETE' });
      setContacts(contacts.filter(c => c.id !== id));
      toast.success('Contact removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  // Strip HTML tags for preview - using DOMPurify for safety
  const stripHtml = (html) => {
    const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    return sanitized;
  };

  // Sanitize HTML for rendering
  const sanitizeHtml = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'a', 'span', 'div', 'img', 'table', 'tr', 'td', 'th', 'thead', 'tbody'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target']
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-email-campaigns">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Email Campaigns
          </h1>
          <p className="text-slate-500 mt-1">Manage sequences and marketing emails</p>
        </div>
        <Button onClick={() => setBroadcastModal(true)} className="bg-orange-600 hover:bg-orange-700">
          <Send className="w-4 h-4 mr-2" />
          Send Broadcast
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_contacts + stats.total_leads}</p>
                <p className="text-xs text-slate-500">Total Subscribers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Play className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active_sequences}</p>
                <p className="text-xs text-slate-500">Active Sequences</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.pending_emails}</p>
                <p className="text-xs text-slate-500">Pending Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.sent_emails}</p>
                <p className="text-xs text-slate-500">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="sequences">Email Sequences</TabsTrigger>
          <TabsTrigger value="contacts">Marketing List</TabsTrigger>
        </TabsList>

        {/* Sequences Tab */}
        <TabsContent value="sequences">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openSequenceModal()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              New Sequence
            </Button>
          </div>

          {sequences.length === 0 ? (
            <Card className="border-none shadow-sm">
              <CardContent className="py-16 text-center">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No email sequences yet</p>
                <Button onClick={() => openSequenceModal()} className="mt-4 bg-orange-600 hover:bg-orange-700">
                  Create Your First Sequence
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {sequences.map((seq) => (
                <Card key={seq.id} className="border-none shadow-sm overflow-hidden">
                  {/* Sequence Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedSequence(expandedSequence === seq.id ? null : seq.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{seq.name}</h3>
                          <Badge variant={seq.is_active ? 'default' : 'secondary'}>
                            {seq.is_active ? 'Active' : 'Paused'}
                          </Badge>
                          <Badge variant="outline">
                            {TRIGGERS.find(t => t.value === seq.trigger)?.label || seq.trigger}
                          </Badge>
                        </div>
                        {seq.description && (
                          <p className="text-sm text-slate-500 mb-2">{seq.description}</p>
                        )}
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">{seq.steps?.length || 0}</span> emails in sequence
                          <span className="mx-2">•</span>
                          <span className="text-orange-600">Click to {expandedSequence === seq.id ? 'collapse' : 'view emails'}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleSequence(seq)}
                          title={seq.is_active ? 'Pause' : 'Activate'}
                        >
                          {seq.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openSequenceModal(seq)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteSequence(seq.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedSequence === seq.id ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Email Details */}
                  {expandedSequence === seq.id && seq.steps && seq.steps.length > 0 && (
                    <div className="border-t bg-slate-50 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-slate-700">Email Sequence Steps</h4>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openSequenceModal(seq)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit All Emails
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {seq.steps.map((step, idx) => (
                          <Card key={idx} className="border shadow-sm bg-white">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                {/* Day Badge */}
                                <div className="flex-shrink-0 w-16 text-center">
                                  <div className="bg-orange-100 text-orange-700 rounded-lg py-2 px-3">
                                    <p className="text-xs font-medium">Day</p>
                                    <p className="text-xl font-bold">{step.delay_days}</p>
                                  </div>
                                </div>
                                
                                {/* Email Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Email {idx + 1} Subject:</p>
                                      <h5 className="font-semibold text-slate-900">{step.subject || '(No subject)'}</h5>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openPreview(step, idx)}
                                      className="flex-shrink-0"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      Preview
                                    </Button>
                                  </div>
                                  
                                  {/* Content Preview */}
                                  <div className="bg-slate-50 rounded-lg p-3 mt-2">
                                    <p className="text-xs text-slate-500 mb-1">Content Preview:</p>
                                    <p className="text-sm text-slate-600 line-clamp-3">
                                      {stripHtml(step.html_content).substring(0, 200)}
                                      {stripHtml(step.html_content).length > 200 ? '...' : ''}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setImportModal(true)} variant="outline" className="mr-2">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
          </div>

          <Card className="border-none shadow-sm">
            <CardContent className="p-0">
              {contacts.length === 0 ? (
                <div className="py-16 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No marketing contacts yet</p>
                  <p className="text-sm text-slate-400 mt-1">Contacts are automatically added when users register</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Name</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Source</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {contacts.slice(0, 50).map((contact) => (
                        <tr key={contact.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{contact.email}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{contact.name || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">{contact.source}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={contact.subscribed ? 'default' : 'secondary'}>
                              {contact.subscribed ? 'Subscribed' : 'Unsubscribed'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteContact(contact.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contacts.length > 50 && (
                    <p className="text-center text-sm text-slate-500 py-4">
                      Showing 50 of {contacts.length} contacts
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sequence Edit Modal */}
      <Dialog open={sequenceModal} onOpenChange={setSequenceModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSequence ? 'Edit Email Sequence' : 'Create Email Sequence'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Sequence Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="seq-name">Sequence Name</Label>
                <Input
                  id="seq-name"
                  value={sequenceForm.name}
                  onChange={(e) => setSequenceForm(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1"
                  placeholder="e.g., Welcome Sequence"
                />
              </div>
              <div>
                <Label htmlFor="seq-trigger">Trigger</Label>
                <Select 
                  value={sequenceForm.trigger} 
                  onValueChange={(val) => setSequenceForm(prev => ({ ...prev, trigger: val }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGERS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="seq-desc">Description (optional)</Label>
              <Input
                id="seq-desc"
                value={sequenceForm.description}
                onChange={(e) => setSequenceForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={sequenceForm.is_active}
                onCheckedChange={(checked) => setSequenceForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Active (starts sending immediately when triggered)</Label>
            </div>

            {/* Email Steps - Always Expanded */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-lg font-semibold">Email Steps</Label>
                  <p className="text-sm text-slate-500">Each email is sent after the specified number of days</p>
                </div>
                <Button type="button" onClick={addStep} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-1" /> Add Email
                </Button>
              </div>
              
              {sequenceForm.steps.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No emails added yet</p>
                  <Button type="button" onClick={addStep} className="mt-3 bg-orange-600 hover:bg-orange-700">
                    <Plus className="w-4 h-4 mr-1" /> Add First Email
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {sequenceForm.steps.map((step, idx) => (
                    <Card key={idx} className="border-2 border-slate-200">
                      <CardHeader className="py-3 px-4 bg-slate-50 border-b">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-orange-600">Email {idx + 1}</Badge>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Send on Day:</Label>
                              <Input
                                type="number"
                                min="0"
                                value={step.delay_days}
                                onChange={(e) => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)}
                                className="w-20 h-8"
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStep(idx)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 space-y-4">
                        <div>
                          <Label htmlFor={`subject-${idx}`}>Subject Line</Label>
                          <Input
                            id={`subject-${idx}`}
                            value={step.subject}
                            onChange={(e) => updateStep(idx, 'subject', e.target.value)}
                            className="mt-1"
                            placeholder="Enter email subject..."
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label htmlFor={`content-${idx}`}>Email Content (HTML)</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openPreview(step, idx)}
                            >
                              <Eye className="w-4 h-4 mr-1" /> Preview
                            </Button>
                          </div>
                          <Textarea
                            id={`content-${idx}`}
                            value={step.html_content}
                            onChange={(e) => updateStep(idx, 'html_content', e.target.value)}
                            className="mt-1 font-mono text-sm"
                            rows={12}
                            placeholder="<p>Hello {{name}},</p>&#10;&#10;<p>Your email content here...</p>"
                          />
                          <p className="text-xs text-slate-500 mt-2">
                            Use <code className="bg-slate-100 px-1 rounded">{"{{name}}"}</code> to personalize with recipient's name
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSequenceModal(false)}>Cancel</Button>
            <Button onClick={saveSequence} className="bg-orange-600 hover:bg-orange-700">
              <Save className="w-4 h-4 mr-1" />
              {editingSequence ? 'Save Changes' : 'Create Sequence'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Modal */}
      <Dialog open={previewModal} onOpenChange={setPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview - Email {previewEmail?.index}</DialogTitle>
          </DialogHeader>
          {previewEmail && (
            <div className="py-4">
              <div className="bg-slate-100 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-500">Subject:</p>
                <p className="font-semibold text-slate-900">{previewEmail.subject || '(No subject)'}</p>
              </div>
              <div className="border rounded-lg p-4 bg-white">
                <p className="text-sm text-slate-500 mb-2">Email Body:</p>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewEmail.html_content || '<p>No content</p>') }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Modal */}
      <Dialog open={broadcastModal} onOpenChange={setBroadcastModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Broadcast Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Send To</Label>
              <Select 
                value={broadcastForm.target} 
                onValueChange={(val) => setBroadcastForm(prev => ({ ...prev, target: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subscribers ({stats.total_contacts + stats.total_leads})</SelectItem>
                  <SelectItem value="leads">Leads Only ({stats.total_leads})</SelectItem>
                  <SelectItem value="contacts">Marketing Contacts Only ({stats.total_contacts})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="broadcast-subject">Subject</Label>
              <Input
                id="broadcast-subject"
                value={broadcastForm.subject}
                onChange={(e) => setBroadcastForm(prev => ({ ...prev, subject: e.target.value }))}
                className="mt-1"
                placeholder="Special offer for our valued clients!"
              />
            </div>
            <div>
              <Label htmlFor="broadcast-content">Email Content (HTML)</Label>
              <Textarea
                id="broadcast-content"
                value={broadcastForm.html_content}
                onChange={(e) => setBroadcastForm(prev => ({ ...prev, html_content: e.target.value }))}
                className="mt-1 font-mono text-sm"
                rows={10}
                placeholder="<p>Hello {{name}},</p>"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use {"{{name}}"} to personalize with recipient's name
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcastModal(false)}>Cancel</Button>
            <Button onClick={sendBroadcast} disabled={sending} className="bg-orange-600 hover:bg-orange-700">
              {sending ? 'Sending...' : 'Send Broadcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={importModal} onOpenChange={setImportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Marketing Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Upload a CSV file with the following columns:
            </p>
            <div className="bg-slate-50 p-3 rounded-lg">
              <code className="text-sm text-slate-700">email, name, phone, city</code>
            </div>
            <p className="text-xs text-slate-500">
              Only email is required. Duplicate emails will be skipped.
            </p>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              className="hidden"
              onChange={handleImport}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Select CSV File'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
